/**
 * Read-only photo gallery for V2 detail screens.
 *
 * Renders a hero cover image plus a thumbnail strip below. Clicking a
 * thumbnail swaps the cover. Click the cover to open a full-screen
 * lightbox. Empty state guides editors to the Edit sheet so they
 * understand where uploads go.
 *
 * Used by V2 PropertyDetail and V2 RoomDetail above the tabs — the
 * editor's first answer to "where are the photos I uploaded?"
 */
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, ImagePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { extractImageUrls } from '@/lib/v2-images'
import { useLanguage } from '@/contexts/LanguageContext'

type Props = {
  /** Raw JSONB column value (string[] | {url, alt}[] | unknown). */
  images: unknown
  /** Used as <img alt> + lightbox label. */
  title: string
  /** Optional CTA shown in the empty state — typically "Upload photos via Edit". */
  emptyAction?: React.ReactNode
  /** Aspect ratio for the cover. Defaults to 16/9. Use 4/3 for tighter screens. */
  aspect?: 'video' | '4/3'
}

export function DetailPhotoGallery({ images, title, emptyAction, aspect = 'video' }: Props) {
  const { t } = useLanguage()
  const urls = extractImageUrls(images)
  const [activeIdx, setActiveIdx] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Keep the active index inside bounds when the underlying list shrinks
  useEffect(() => {
    if (activeIdx >= urls.length) setActiveIdx(0)
  }, [urls.length, activeIdx])

  // Lightbox keyboard nav
  useEffect(() => {
    if (!lightboxOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false)
      if (e.key === 'ArrowLeft' && urls.length > 1) {
        setActiveIdx((i) => (i === 0 ? urls.length - 1 : i - 1))
      }
      if (e.key === 'ArrowRight' && urls.length > 1) {
        setActiveIdx((i) => (i === urls.length - 1 ? 0 : i + 1))
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [lightboxOpen, urls.length])

  // ── Empty state ─────────────────────────────────────────────────────
  if (urls.length === 0) {
    return (
      <Card className="border-dashed p-6 flex flex-col sm:flex-row items-center gap-4">
        <div className="rounded-full bg-muted p-3 shrink-0" aria-hidden="true">
          <ImagePlus className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h3 className="text-sm font-semibold">
            {t('admin.empty.noPhotosTitle', 'No photos uploaded yet')}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t(
              'admin.empty.noPhotosDesc',
              "Upload photos via the Edit panel — drop multiple images, set a primary, and they'll appear here for everyone to see.",
            )}
          </p>
        </div>
        {emptyAction && <div className="shrink-0">{emptyAction}</div>}
      </Card>
    )
  }

  const aspectClass = aspect === '4/3' ? 'aspect-[4/3]' : 'aspect-video'
  const cover = urls[activeIdx] ?? urls[0]

  return (
    <>
      <div className="space-y-2">
        {/* Cover */}
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className={`relative ${aspectClass} w-full rounded-lg overflow-hidden bg-muted group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
          aria-label={`View ${title} photo ${activeIdx + 1} fullscreen`}
        >
          <img
            src={cover}
            alt={`${title} — photo ${activeIdx + 1}`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
          {urls.length > 1 && (
            <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs font-medium px-2 py-0.5 rounded">
              {activeIdx + 1} / {urls.length}
            </span>
          )}
        </button>

        {/* Thumbnail strip */}
        {urls.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:thin]">
            {urls.map((u, i) => (
              <button
                key={`${u}-${i}`}
                type="button"
                onClick={() => setActiveIdx(i)}
                className={`shrink-0 h-16 w-24 rounded-md overflow-hidden border-2 transition-all ${
                  i === activeIdx
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent opacity-70 hover:opacity-100'
                }`}
                aria-label={`Show photo ${i + 1}`}
                aria-pressed={i === activeIdx}
              >
                <img
                  src={u}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`${title} photos`}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/10 hover:text-white"
            onClick={(e) => {
              e.stopPropagation()
              setLightboxOpen(false)
            }}
            aria-label={t('admin.aria.closeLightbox', 'Close lightbox')}
          >
            <X className="h-5 w-5" />
          </Button>

          <img
            src={cover}
            alt={`${title} — photo ${activeIdx + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {urls.length > 1 && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 hover:text-white h-12 w-12"
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveIdx((i) => (i === 0 ? urls.length - 1 : i - 1))
                }}
                aria-label={t('admin.aria.previousPhoto', 'Previous photo')}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 hover:text-white h-12 w-12"
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveIdx((i) => (i === urls.length - 1 ? 0 : i + 1))
                }}
                aria-label={t('admin.aria.nextPhoto', 'Next photo')}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
              <span
                className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm font-medium px-3 py-1 rounded"
                onClick={(e) => e.stopPropagation()}
              >
                {activeIdx + 1} / {urls.length}
              </span>
            </>
          )}
        </div>
      )}
    </>
  )
}
